import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  Cancel,
  CheckCircle,
  Email,
  HourglassEmpty,
  Person,
  Phone,
} from "@mui/icons-material";
import IndiaProviderProfileDialog from "../../components/india/IndiaProviderProfileDialog";
import { listIndiaProviders } from "../../utils/indiaAdminService";

const formatLocation = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address;
  return [address.city, address.state].filter(Boolean).join(", ");
};

const formatDate = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleDateString("en-IN");
};

/**
 * India "Request" page: pending provider applications, same layout as the
 * Australian ClientApprovals page.
 */
const IndiaClientApprovals = () => {
  const [pendingClients, setPendingClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState({ open: false, client: null, mode: "view" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchPendingClients = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listIndiaProviders("pending");
      setPendingClients(result.providers || []);
    } catch (err) {
      setError(`Failed to fetch pending client applications: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingClients();
  }, []);

  const openDialog = (client, mode) => setDialog({ open: true, client, mode });

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography>Loading pending applications...</Typography>
      ) : pendingClients.length === 0 ? (
        !error && (
          <Alert severity="info">
            <Typography variant="body1">
              Great! No pending client applications at the moment.
            </Typography>
          </Alert>
        )
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>
                {pendingClients.length} client application
                {pendingClients.length !== 1 ? "s" : ""} pending review
              </strong>
              <br />
              Aadhaar and PAN documents must be approved before an application can be approved.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {pendingClients.map((client) => (
              <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={client.id}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Person sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="h6" component="div">
                          {client.name || "Name Not Provided"}
                        </Typography>
                      </Box>
                      <Chip
                        icon={<HourglassEmpty />}
                        label="Pending Review"
                        color="warning"
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Email sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {client.email || "Email Not Provided"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Phone sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                        <Typography variant="body2">
                          {client.phone || "Phone Not Provided"}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Application
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Location:</strong> {formatLocation(client.address) || "Not specified"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Applied:</strong> {formatDate(client.createdAt)}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={() => openDialog(client, "approve")}
                        fullWidth
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Cancel />}
                        onClick={() => openDialog(client, "reject")}
                        fullWidth
                      >
                        Reject
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => openDialog(client, "view")}
                        fullWidth
                      >
                        View
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <IndiaProviderProfileDialog
        open={dialog.open}
        providerId={dialog.client?.id}
        providerName={dialog.client?.name}
        mode={dialog.mode}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        onStatusChanged={(id) => setPendingClients((prev) => prev.filter((c) => c.id !== id))}
        notify={showSnackbar}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IndiaClientApprovals;
